import { camelCase } from 'lodash';
import React from 'react';

import { Stack } from '@nerdgeschoss/shimmer-component-stack';
import { Effect } from '../../../components/Effect/Effect';
import { FormRangepicker } from '../../../components/Form/Datepicker/FormRangepicker';
import { FormMultiSelect } from '../../../components/Form/Select/FormMultiSelect';
import { FormTagsInput } from '../../../components/Form/TagsInput/FormTagsInput';
import { SalesDonut } from '../../../components/SalesDonut/SalesDonut';
import { ColumnContent, SortableList } from '../../../components/SortableList/SortableList';
import { SortByIcon } from '../../../components/SortableList/SortByIcon';
import { ViewportObserver } from '../../../components/ViewportObserver/ViewportObserver';
import { t } from '../../../locales/translate';
import { Event, getEventName, getEventSalesStatus } from '../../../model/event';
import { useQueryAsState } from '../../../model/history';
import { eventPath } from '../../../routes';
import { formatDate } from '../../../util/util';

interface Props {
  events: Event[];
  error?: Error;
  loading?: boolean;
  displaySearch?: boolean;
  columns?: Array<Extract<keyof Event, string>>;
  emptyMessage?: string;
}

export function EventsList({
  events,
  error,
  loading,
  displaySearch = true,
  columns,
  emptyMessage,
}: Props): JSX.Element {
  const [params, setParams] = useQueryAsState();

  function renderColumn(column: ColumnContent<Event>): boolean {
    if (!columns || columns.includes(column.dataKey)) {
      return true;
    }
    return false;
  }

  const columnsList: Array<ColumnContent<Event>> = [
    {
      width: 50,
      label: t.model.datetime._name,
      sortable: true,
      apiSortIcon: <SortByIcon sortBy="happening_from" />,
      dataKey: 'happeningFrom',
      cellRenderer: (row) => {
        const formattedFrom = formatDate(row.happeningFrom);
        const formattedUntil = row.happeningUntil ? formatDate(row.happeningUntil) : formattedFrom;

        return (
          <div className="events__dates">
            <span>{formattedFrom}</span>
            {formattedFrom !== formattedUntil && <span> - {formattedUntil}</span>}
          </div>
        );
      },
    },
    {
      width: 120,
      label: t.model.event._name,
      dataKey: 'name',
      cellRenderer: (row) => getEventName(row),
    },
    {
      width: 150,
      label: t.model.venue._name,
      dataKey: 'venue',
      cellRenderer: (row) =>
        row.venue && [row.venue.name, row.venue.address && row.venue.address.city].filter(Boolean).join(' - '),
    },
    {
      width: 80,
      label: t.model.event.owner,
      dataKey: 'owningCompany',
      cellRenderer: (row) => row.owningCompany && row.owningCompany.name,
    },
    {
      width: 80,
      label: t.model.status._name,
      dataKey: 'status',
      cellRenderer: (row) => t.model.status && t.model.status[camelCase(row.status)],
    },
    {
      width: 80,
      label: t.model.reference._name,
      dataKey: 'sku',
    },
    {
      width: 80,
      label: t.model.sale.salesStats,
      dataKey: 'salesStats',
      cellRenderer: (row) => (
        <ViewportObserver onEnter={() => getEventSalesStatus(row.id)}>
          <Effect>{row.salesStats && <SalesDonut data={row.salesStats} size="table" />}</Effect>
        </ViewportObserver>
      ),
    },
  ];

  const selectOptions = [
    {
      id: 'on_sale',
      label: t.model.status.onSale,
    },
    {
      id: 'draft',
      label: t.model.status.draft,
    },
    {
      id: 'in_review',
      label: t.model.status.inReview,
    },
    {
      id: 'stopped',
      label: t.model.status.stopped,
    },
  ];

  return (
    <SortableList
      emptyMessage={emptyMessage}
      items={events}
      error={error}
      actions={
        displaySearch && (
          <Stack gap={20} line wrap>
            <FormTagsInput
              value={params.query?.map((q) => q.label)}
              onChange={(val) => {
                if (val) {
                  setParams({
                    query: val.map((v) => ({ exclude: false, label: v })),
                  });
                }
              }}
              icon="search--sm"
              maxWidth={400}
            />

            <FormRangepicker
              icon="calendar--sm"
              label={t.model.datetime.timeframe}
              maxWidth={250}
              hourpicker
              value={params.timeframe}
              onChange={(value) => {
                setParams({
                  timeframe: value,
                });
              }}
            />

            <FormMultiSelect
              options={selectOptions}
              label={t.model.status._name}
              icon="activity--sm"
              allowEmpty
              maxWidth={250}
              onChange={(value) => {
                setParams({
                  status: value || undefined,
                });
              }}
              value={params.status}
            />
          </Stack>
        )
      }
      loading={loading}
      rowLink={(row) => eventPath(row)}
      columns={columnsList.filter((column) => renderColumn(column))}
    />
  );
}
